const PAYMENT_METHODS = [
  { value: 'cash', label: 'پیش خرید' },
  { value: 'transfer', label: 'تسویه روی باسکول' },
  { value: 'check', label: 'چک' },
  { value: 'credit', label: 'سایر روش ها' }
]

export const useSupportChat = () => {
  const isOpen = ref(false)
  const step = ref('form')
  const ticketId = ref(null)
  const messages = ref([])
  const unreadCount = ref(0)
  const isLoading = ref(false)
  const isSending = ref(false)
  const notificationPermission = ref('default')
  const searchResults = ref([])
  const searchLoading = ref(false)
  const searchFocused = ref(false)
  const categoryDropdownVisible = ref(false)
  const { t } = useI18n()

  let searchTimeout = null

  const form = reactive({
    name: '',
    mobile: '',
    categories: [],
    categorySearch: '',
    weight: '',
    payment_method: '',
    last_page_id:'', 
    last_visit:'', 
  })

  const filteredPaymentMethods = PAYMENT_METHODS

  const isFormValid = computed(() =>
    form.name.trim() &&
    /^0\d{10}$/.test(form.mobile.trim()) &&
    (form.categories.length > 0 || form.categorySearch.trim().length >= 3) &&
    form.weight.trim() &&
    /^\d+(\.\d+)?$/.test(form.weight.trim()) &&
    form.payment_method
  )

  let eventSource = null

  function open() {
    isOpen.value = true
  }

  function close() {
    isOpen.value = false
    unreadCount.value = 0
  }

  function selectCategory(item) {
    const exists = form.categories.some(c => c.value === item.value)
    if (!exists) {
      form.categories.push({ value: item.value, label: item.label })
    }
    form.categorySearch = ''
    searchResults.value = []
    categoryDropdownVisible.value = false
  }

  function removeCategory(cat) {
    const idx = form.categories.findIndex(c => c.value === cat.value)
    if (idx !== -1) form.categories.splice(idx, 1)
  }

  function searchCategories(query) {
    if (searchTimeout) clearTimeout(searchTimeout)
    if (query.trim().length < 3) {
      searchResults.value = []
      categoryDropdownVisible.value = false
      return
    }
    searchTimeout = setTimeout(async () => {
      searchLoading.value = true
      try {
        const res = await $fetch('https://sarvban.com/app/api/anar/search-category/', {
          method: 'POST',
          body: { word: query.trim() }
        })
        const data = typeof res === 'string' ? JSON.parse(res) : res
        searchResults.value = Array.isArray(data) ? data : []
        categoryDropdownVisible.value = searchFocused.value && data.length > 0
      } catch {
        searchResults.value = []
        categoryDropdownVisible.value = false
      } finally {
        searchLoading.value = false
      }
    }, 300)
  }

  function onCategorySearchFocus() {
    searchFocused.value = true
    if (searchResults.value.length > 0) {
      categoryDropdownVisible.value = true
    }
  }

  function onCategorySearchBlur() {
    setTimeout(() => {
      searchFocused.value = false
      categoryDropdownVisible.value = false
    }, 200)
  }

  function resetForm() {
    step.value = 'form'
    form.name = ''
    form.mobile = ''
    form.categories = []
    form.categorySearch = ''
    form.weight = ''
    form.payment_method = ''
    messages.value = []
    ticketId.value = null
    searchResults.value = []
    disconnectSSE()
  }

  function getCategoryPayload() {
    const payload = []
    for (const c of form.categories) {
      payload.push(c.value || c.label)
    }
    const rawText = form.categorySearch.trim()
    if (rawText) payload.push(rawText)
    // Prefer raw text (most specific user input), then first selected category, then default
    if (rawText) return rawText
    if (payload.length > 0) return payload[0]
    return 'عمومی'
  }

  async function submitForm() {
    if (!isFormValid.value) return false
    isLoading.value = true
    
    try {
      const res = await $fetch('/market/api/messages/gofti', {
        method: 'POST',
        body: {
          username: form.name,
          mobile: form.mobile,
          product_name: getCategoryPayload(),
          weight: form.weight,
          pay: form.payment_method,
          last_page_id : form.last_page_id,
          last_visit : form.last_visit, 
        }
      })
      if (res && res.success !== false) {
        ticketId.value = res.ticket_id
        messages.value.push({
          id: `welcome_${Date.now()}`,
          message: t('details.welcome_message'),
          sender: 'support',
          timestamp: Date.now()
        })
        step.value = 'chat'
        requestNotificationPermission()
        registerServiceWorker()
        connectSSE()
      }
      return res && res.success !== false
    } catch {
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function sendMessage(text) {
    if (!text.trim() || !ticketId.value) return
    isSending.value = true
    const tempId = `temp_${Date.now()}`
    messages.value.push({
      id: tempId, message: text, sender: 'user', timestamp: Date.now()
    })
    try {
      const res = await $fetch(`/market/api/support/${ticketId.value}/send`, {
        method: 'POST',
        body: { message: text, sender: 'user' }
      })
      const idx = messages.value.findIndex(m => m.id === tempId)
      if (idx !== -1) {
        messages.value[idx].id = res.message.id
        messages.value[idx].timestamp = res.message.timestamp
      }
    } catch {
      console.error('Send message failed')
    } finally {
      isSending.value = false
    }
  }

  function connectSSE() {
    if (!ticketId.value || eventSource) return
    eventSource = new EventSource(`/market/api/support/${ticketId.value}/subscribe`, { withCredentials: true })
    eventSource.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data)
        if (data.type === 'message' && data.sender !== 'user') {
          if (!messages.value.some(m => m.id === data.id)) {
            messages.value.push({
              id: data.id, message: data.message,
              sender: data.sender, timestamp: data.timestamp
            })
            if (!isOpen.value) {
              unreadCount.value++
              showNotification('سروبان | پشتیبانی', data.message)
            }
          }
        }
      } catch { /* ignore parse errors */ }
    }
    eventSource.onerror = () => {
      eventSource?.close()
      eventSource = null
      setTimeout(() => connectSSE(), 5000)
    }
  }

  function disconnectSSE() {
    if (eventSource) {
      eventSource.close()
      eventSource = null
    }
  }

  async function requestNotificationPermission() {
    if (!process.client || !('Notification' in window)) return
    if (Notification.permission === 'granted') {
      notificationPermission.value = 'granted'
      return
    }
    try {
      const result = await Notification.requestPermission()
      notificationPermission.value = result
    } catch {
      notificationPermission.value = 'denied'
    }
  }

  async function registerServiceWorker() {
    await registerPwaServiceWorker()
  }

  function showNotification(title, body) {
    if (!process.client || !('Notification' in window)) return
    if (Notification.permission !== 'granted') return
    try {
      new Notification(title, {
        body,
        icon: 'https://sarvban.com/wp-content/uploads/2019/10/android-icon-36x36.png',
        tag: 'sarvban-support',
        vibrate: [200, 100, 200]
      })
    } catch { /* ignore */ }
  }

  onUnmounted(() => disconnectSSE())

  return reactive({
    isOpen, step, ticketId, messages, unreadCount,
    isLoading, isSending, form,
    searchResults, searchLoading, searchFocused, categoryDropdownVisible,
    filteredPaymentMethods, isFormValid,
    notificationPermission,
    open, close, resetForm,
    selectCategory, removeCategory,
    searchCategories, onCategorySearchFocus, onCategorySearchBlur,
    submitForm, sendMessage,
    registerServiceWorker
  })
}
